import ChannelHeader from "anchor-ui/channel-header";
import "./styles.css";
import { useEffect, useState, useRef } from "react";
import { Functions, httpsCallable } from "firebase/functions";
import Card from "anchor-ui/card";
import IconMenu from "anchor-ui/icon-menu";
import MenuItem from "anchor-ui/menu-item";
import { IconMore, IconHeart } from "anchor-ui/icons";
import Input from "anchor-ui/input";
import { checkIfUserIsSuperAdmin } from "../../utils/checkIfUserIsAdmin";
import { getSecondaryColor } from "../../utils/activeColor";
import Button from "anchor-ui/button";

const BanList = ({
  functions,
  setAlert,
  loggedInUser,
}: {
  functions: Functions;
  setAlert: React.Dispatch<React.SetStateAction<{ message: string; type: string } | null>>;
  loggedInUser: CometChat.User,
}) => {
  const [bannedItems, setBannedItems] = useState<any>([]);
  const [searchInput, setSearchInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<any>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewMode, setViewMode] = useState<"users" | "ips">("users");

  // Reference to this component's top element
  const componentRef = useRef<HTMLElement>(null);

  const itemsPerPage = 100;

  useEffect(() => {
    // Reset pagination when switching view modes
    setCurrentPage(1);
    setBannedItems([]);

    if (viewMode === "users") {
      getBannedUsersAsync();
    } else {
      getBannedIpsAsync();
    }
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === "users") {
      getBannedUsersAsync();
    } else {
      getBannedIpsAsync();
    }
  }, [currentPage]);

  // Scroll parent containers when the page changes
  useEffect(() => {
    if (componentRef.current) {
      // Try to find all scrollable parent containers
      scrollParentToTop(componentRef.current);
    }
  }, [currentPage]);

  // Function to find scrollable parent and scroll it to top
  const scrollParentToTop = (element: HTMLElement) => {
    // Try to find the parent scrollable container
    let parent = element.parentElement;

    // Keep track if we've scrolled something
    let scrolled = false;

    // Check up to 10 levels of parents
    for (let i = 0; i < 10; i++) {
      if (!parent) break;

      // Check if this parent has scrolling
      if (
        parent.scrollHeight > parent.clientHeight ||
        getComputedStyle(parent).overflowY === 'auto' ||
        getComputedStyle(parent).overflowY === 'scroll'
      ) {
        // We found a scrollable parent, scroll it
        parent.scrollTop = 0;
        scrolled = true;
        break;
      }

      parent = parent.parentElement;
    }

    // If we didn't find any scrollable parent, try a last resort approach
    if (!scrolled) {
      // This affects the main document scroll
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  };

  const getBannedUsersAsync = async ({ filterWord }: { filterWord?: string } = {}) => {
    try {
      const fetchBannedUsers = httpsCallable(functions, "fetchBannedUsers");
      const result = await fetchBannedUsers({ perPage: itemsPerPage, page: currentPage, filterWord });
      const data = result.data as { users: any[]; total: number };

      console.log("Fetched banned users:", data.users);
      setBannedItems(data.users);
      setTotalItems(data.total);
    } catch (error) {
      console.error("Error fetching banned users:", error);
      setAlert({ message: "Er is een fout opgetreden bij het ophalen van verbannen gebruikers", type: "error" });
    }
  };

  const getBannedIpsAsync = async ({ filterWord }: { filterWord?: string } = {}) => {
    try {
      const fetchBannedIps = httpsCallable(functions, "fetchBannedIps");
      const result = await fetchBannedIps({ perPage: itemsPerPage, page: currentPage, filterWord });
      const data = result.data as { ips: any[]; total: number };

      console.log("Fetched banned IPs:", data.ips);
      setBannedItems(data.ips);
      setTotalItems(data.total);
    } catch (error) {
      console.error("Error fetching banned IPs:", error);
      setAlert({ message: "Er is een fout opgetreden bij het ophalen van verbannen IP-adressen", type: "error" });
    }
  };

  const getDateTimeFromUnix = (unix: number) => {
    const date = new Date(unix * 1000);
    const formattedDate = date.getDate().toString().padStart(2, '0') + "-" + (date.getMonth() + 1).toString().padStart(2, '0') + "-" + date.getFullYear();
    const formattedTime = date.getHours().toString().padStart(2, '0') + ":" + date.getMinutes().toString().padStart(2, '0');
    return formattedDate + " " + formattedTime;
  };

  const unbanUser = async (uid: string) => {
    try {
      const unbanUser = httpsCallable(functions, "unbanUser");
      await unbanUser({ uid });

      // Remove user from bannedItems
      setBannedItems(bannedItems.filter((item: any) => item.uid !== uid));
      setAlert({ message: "Gebruiker succesvol geunbanned", type: "success" });
    } catch (error) {
      console.error("Error unbanning user:", error);
      setAlert({ message: "Er is iets fout gegaan bij het unbannen van de gebruiker", type: "error" });
    }
  };

  const unbanIp = async (ip: string) => {
    try {
      const unbanIp = httpsCallable(functions, "unbanIp");
      await unbanIp({ ip });

      // Remove IP from bannedItems
      setBannedItems(bannedItems.filter((item: any) => item.ip !== ip));
      setAlert({ message: "IP-adres succesvol geunbanned", type: "success" });
    } catch (error) {
      console.error("Error unbanning IP:", error);
      setAlert({ message: "Er is iets fout gegaan bij het unbannen van het IP-adres", type: "error" });
    }
  };

  const searchItems = async (searchTerm: string) => {
    // if input is less than 3 characters, don't search
    if (searchTerm.length < 3 && searchTerm.length > 0) {
      setIsSearching(false);
      return;
    }

    if (viewMode === "users") {
      await getBannedUsersAsync({ filterWord: searchTerm });
    } else {
      await getBannedIpsAsync({ filterWord: searchTerm });
    }

    setIsSearching(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    setIsSearching(true);
    setBannedItems([]);
    setTypingTimeout(setTimeout(() => searchItems(value), 500));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Attempt immediate scroll
    if (componentRef.current) {
      scrollParentToTop(componentRef.current);
    }
  };

  const handleNextPage = () => {
    if (currentPage * itemsPerPage < totalItems) {
      setCurrentPage(currentPage + 1);
      // Attempt immediate scroll
      if (componentRef.current) {
        scrollParentToTop(componentRef.current);
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      // Attempt immediate scroll
      if (componentRef.current) {
        scrollParentToTop(componentRef.current);
      }
    }
  };

  // Toggle between users and IPs view
  const toggleViewMode = () => {
    setViewMode(viewMode === "users" ? "ips" : "users");
    setSearchInput("");
  };

  // Calculate the range of items displayed on the current page
  const startItemIndex = (currentPage - 1) * itemsPerPage + 1;
  const endItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

  // Calculate total number of pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];

    // Always show first page
    pageNumbers.push(1);

    // Calculate range of pages to show around current page
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pageNumbers.push('...');
    }

    // Add pages in the middle
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pageNumbers.push('...');
    }

    // Add last page if there is more than one page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  // Render different content based on view mode
  const renderBannedItem = (item: any, index: number) => {
    // Calculate the actual index based on the current page
    const itemNumber = (currentPage - 1) * itemsPerPage + index + 1;

    if (viewMode === "users") {
      return (
        <Card className="admin-banlist-card" key={index}>
          <div className="admin-banlist-card-content">
            {
              checkIfUserIsSuperAdmin(loggedInUser) &&
              <IconMenu icon={<IconMore />} className="unban-button">
                <MenuItem
                  text={`Unban ${item.bannedUsername ?? item.uid}`}
                  icon={<IconHeart />}
                  onClick={() => unbanUser(item.uid)}
                />
              </IconMenu>
            }
            <div className="user-info">
              <p><b>#{itemNumber}</b></p>
              <p>{item.bannedUsername ?? item.uid}</p>
              <p>{item.email}</p>
            </div>
            <div className="ban-details">
              <p><b>Reden:</b> {item.banReason}</p>
              <p><b>Verbannen door:</b> {item.bannedBy ?? "admin"}</p>
              <p><b>Verbannen vanaf:</b> {item.bannedAt ? getDateTimeFromUnix(item.bannedAt._seconds) : "N/A"}</p>
              <p><b>Verbannen tot:</b> {getDateTimeFromUnix(item.bannedUntil._seconds)}</p>
              <br />
              <p><b>IP:</b>
                <ul>
                  {Array.isArray(item.ip) ? (
                    item.ip.map((ip: string, index: number) => <li key={index}>{ip}</li>)
                  ) : (
                    <li>{item.ip}</li>
                  )}
                </ul>
              </p>
            </div>
          </div>
        </Card>
      );
    } else {
      // IP view
      return (
        <Card className="admin-banlist-card" key={index}>
          <div className="admin-banlist-card-content">
            {
              checkIfUserIsSuperAdmin(loggedInUser) &&
              <IconMenu icon={<IconMore />} className="unban-button">
                <MenuItem
                  text={`Unban ${item.ip}`}
                  icon={<IconHeart />}
                  onClick={() => unbanIp(item.ip)}
                />
              </IconMenu>
            }
            <div className="user-info">
              <p><b>#{itemNumber}</b></p>
              <p><b>IP:</b> {item.ip}</p>
              <p>{item.bannedUsername ? `Gebruiker: ${item.bannedUsername}` : ""}</p>
            </div>
            <div className="ban-details">
              <p><b>Reden:</b> {item.banReason}</p>
              <p><b>Verbannen door:</b> {item.bannedBy ?? "admin"}</p>
              <p><b>Verbannen vanaf:</b> {item.bannedAt ? getDateTimeFromUnix(item.bannedAt._seconds) : "N/A"}</p>
              <p><b>Verbannen tot:</b> {getDateTimeFromUnix(item.bannedUntil._seconds)}</p>
            </div>
          </div>
        </Card>
      );
    }
  };

  return (
    <section ref={componentRef} className="admin-banlist">
      <ChannelHeader
        name={viewMode === "users" ? "Lijst van verbannen gebruikers" : "Lijst van verbannen IP-adressen"}
        style={{
          backgroundColor: getSecondaryColor(),
          borderColor: getSecondaryColor() !== "white" ? "black" : "#c4c4c4",
        }}
      />

      <div className="admin-banlist-container">
        {/* New three-column grid layout */}
        <div className="filter-controls">
          {/* Left column with button and info */}
          <div className="left-controls">
            <Button
              onClick={toggleViewMode}
              className="toggle-button"
            >
              {viewMode === "users" ? "Toon verbannen IP-adressen" : "Toon verbannen gebruikers"}
            </Button>

            <div className="info-container">
              <p>
                <strong>
                  Totaal verbannen {viewMode === "users" ? "gebruikers" : "IP-adressen"}: <b>{totalItems}</b>
                </strong>
              </p>
              {totalItems > 0 && (
                <p>
                  <strong>Weergave: {startItemIndex} - {endItemIndex}</strong>
                </p>
              )}
            </div>
          </div>

          {/* Center column with search input */}

          <div className="search-container">
            <Input
              label={`Zoek naar een ${viewMode === "users" ? "gebruiker" : "IP-adres"}`}
              value={searchInput}
              onChange={handleInputChange}
              maxLength={500}
            />
          </div>

          {/* Right column (empty for balance) */}
          <div></div>
        </div>

        {/* Loading indicator */}
        {isSearching && <p className="searching-indicator">Zoeken...</p>}

        {/* Render banned items if any */}
        {bannedItems.length > 0 && (
          <div className="admin-banlist-cards">
            {bannedItems.map((item: any, index: number) => renderBannedItem(item, index))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 0 && (
          <div className="pagination-controls">
            <Button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </Button>

            {getPageNumbers().map((page, index) => {
              if (page === '...') {
                return <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>;
              } else {
                return (
                  <Button
                    key={`page-${page}`}
                    onClick={() => handlePageChange(page as number)}
                    className={`pagination-page-button ${currentPage === page ? 'active-page' : ''}`}
                  >
                    {page}
                  </Button>
                );
              }
            })}

            <Button
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              className="pagination-button"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </section>
  );

};

export default BanList;